'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useMemo } from 'react';
import type { LabsRecommendation } from '@/app/(platform)/(subscription-gated)/labs/_engine/types';
import { ExternshipPreviewWidgetModal } from '@/components/externship-preview-widget/externship-preview-widget-modal';
import type { RecommendationItem } from '@/features/recommendations/_api/recomendations.schema';

/**
 * The externship detail overlay on the preview payoff.
 *
 * 🔴 THIS IS THE REAL IN-PRODUCT MODAL (Jay, 2026-08-27). It renders
 * `ExternshipPreviewWidgetModal`, the same component a signed-in user gets when they tap an
 * externship in the recommendations widget: company avatar, title, overview, hero image, the numbered
 * "What you will learn and accomplish" list, and the footer CTA. A cold reader now sees the same thing
 * a member sees, which is the point.
 *
 * That component takes its data as a PROP rather than fetching, which is the only reason this is
 * possible on a public route. The externship detail ENDPOINT is a different story:
 * `externships/{id}/details` attaches a Clerk token and prod answers 401 without one. So the component
 * is reusable and the data is not, and this file is the seam between them.
 *
 * Everything below adapts the pack's curated recommendation into the shape the modal expects. The
 * fields we can fill honestly are filled; the rest are left empty rather than invented, because a
 * plausible-looking fabricated overview on a public page is worse than a short true one.
 */

/**
 * Placeholder id for a recommendation with no live catalog id.
 *
 * The pack's static fallback cards do not all carry `externshipId`, and the modal only uses `id` as a
 * key. It is never used to fetch anything here, so a sentinel is safe. It is negative so that it can
 * never collide with a real externship id if this object is ever passed somewhere that does fetch.
 */
const NO_CATALOG_ID = -1;

/**
 * Turn a Labs recommendation into the shape the product modal renders.
 *
 * `whatYoullLearn` is a flat list of sentences and `responsibilities` wants `{ title, description }`.
 * Each bullet becomes the TITLE with an empty description, so it renders as the numbered headline row
 * and nothing hangs beneath it. That reads as intended rather than as missing content.
 */
function toRecommendationItem(rec: LabsRecommendation): RecommendationItem {
  return {
    categories: [],
    companyLogo: rec.logo,
    companyName: rec.company,
    companyTitle: rec.company,
    details: {
      overview: rec.blurb,
      responsibilities: rec.whatYoullLearn.map(item => ({
        description: '',
        title: item
      })),
      topImage: rec.image
    },
    externshipType: 'STANDARD',
    id: rec.externshipId ?? NO_CATALOG_ID,
    image: rec.image,
    skills: [],
    tags: [...rec.tags],
    title: rec.title
  };
}
export function PreviewMatchModal(t0) {
  const $ = _c(12);
  const {
    onClose,
    onStartTrial,
    rec
  } = t0;
  let t1;
  if ($[0] !== rec) {
    t1 = rec ? toRecommendationItem(rec) : null;
    $[0] = rec;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const externship = t1;
  if (!(rec && externship)) {
    return null;
  }
  let t2;
  if ($[2] !== onClose) {
    t2 = open => {
      if (!open) {
        onClose();
      }
    };
    $[2] = onClose;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onStartTrial || $[5] !== rec) {
    t3 = () => onStartTrial(rec);
    $[4] = onStartTrial;
    $[5] = rec;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = Boolean(rec);
  let t5;
  if ($[7] !== externship || $[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <ExternshipPreviewWidgetModal externship={externship} onOpenChange={t2} onStartNow={t3} open={t4} />;
    $[7] = externship;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}